import React, { useEffect, useState, useContext, useRef } from "react";
import Button from "@mui/material/Button";
import { useNavigate } from "react-router-dom";
import ReactPlayer from "react-player";
import { ThemeAudioContext } from "../../components/ThemeAudioContext";
import axios from "axios";

import { useSearchParams } from "react-router-dom";

const Video = () => {
  const navigate = useNavigate();
  const [videoUrl, setVideoUrl] = useState("");
  const [introAudioUrl, setIntroAudio] = useState("");
  const [playVideo, setPlayVideo] = useState(false);

  const [videoEndedOnce, setVideoEndedOnce] = useState(false);
  const [audioEnded, setAudioEnded] = useState(false);

  const introAudioRef = React.useRef(null);
  const playerRef = React.useRef(null);

  const bgNarration = useRef(null);

  const [searchParams] = useSearchParams();

  const { reduceThemeAudioVolume, restoreThemeAudioVolume } =
    useContext(ThemeAudioContext);

  useEffect(() => {
    const language = searchParams.get("language");
    const validLanguages = ["en", "sl", "ta"];

    if (language && validLanguages.includes(language)) {
      localStorage.setItem("language", language);
    }
  }, [searchParams]);

  useEffect(() => {
    // 🔥 kill background audio
    if (window.__BG_AUDIO__) {
      try {
        window.__BG_AUDIO__.pause();
        window.__BG_AUDIO__.currentTime = 0;
      } catch (e) { }
      window.__BG_AUDIO__ = null;
    }

    // 🔥 kill narration audio
    if (window.__NARRATION_AUDIO__) {
      try {
        window.__NARRATION_AUDIO__.pause();
        window.__NARRATION_AUDIO__.currentTime = 0;
      } catch (e) { }
      window.__NARRATION_AUDIO__ = null;
    }

    // 🔥 also kill any DOM audio just in case
    document.querySelectorAll("audio").forEach((a) => {
      try {
        a.pause();
        a.currentTime = 0;
      } catch (e) { }
    });
  }, []);

  const navigateToLanding = () => {
    const clickAudio = new Audio("https://res.cloudinary.com/giglantis/video/upload/v1777458645/START_TOUR_xzviii.mp3");
    clickAudio.volume = 1;
    clickAudio.play().catch((err) => {
      console.log(err);
    });
    navigate("/copper-gate")
  };

  // 🔥 NEW — If audio ends AND video has ended once, navigate immediately
  useEffect(() => {
    if (videoEndedOnce && audioEnded) {
      navigateToLanding();
    }
  }, [videoEndedOnce, audioEnded]);

  // Fetch on mount
  useEffect(() => {
    getColors();
    getVideos();
    getAudios();
    return () => stopIntroAudio();
  }, []);

  // Stop audio on browser navigation
  useEffect(() => {
    const handlePopState = () => stopIntroAudio();
    window.addEventListener("popstate", handlePopState);
    return () => window.removeEventListener("popstate", handlePopState);
  }, []);

  // Play audio once audio URL is ready
  useEffect(() => {
    if (introAudioUrl && !introAudioRef.current) {
      startBackgroundNarrationAudio();
      playIntroAudio()
    };
  }, [introAudioUrl]);

  const videoReady = () => {
    setPlayVideo(true);
  };

  const getColors = async () => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API}Colors/getColors`
      );

      document.documentElement.style.setProperty(
        "--primary-color",
        response.data.primaryColor
      );
      document.documentElement.style.setProperty(
        "--secondary-color",
        response.data.secondaryColor
      );
    } catch (err) {
      console.error(err);
    }
  };

  const getVideos = async () => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API}Videos/getVideos`
      );

      const introVideo = response.data.introVideo;
      const verticalVideo = response.data.verticalVideo;

      window.cachedVideos = { introVideo, verticalVideo };

      const isPortrait =
        window.matchMedia("(orientation: portrait)").matches ||
        window.innerHeight > window.innerWidth;

      setVideoUrl(introVideo);
    } catch (err) {
      console.error(err);
    }
  };

  const getAudios = async () => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API}Audios/getAudios`
      );
      switch (localStorage.getItem("language")) {
        case "en":
          setIntroAudio(response.data.introAudio);
          break;
        case "sl":
          setIntroAudio(response.data.introAudioSinhala);
          break;
        case "tm":
          setIntroAudio(response.data.introAudioTamil);
          break;
        default:
          break;
      }
    } catch (err) {
      console.error(err);
    }
  };

  const playIntroAudio = () => {
    if (!introAudioUrl || introAudioRef.current) return;

    const audio = new Audio(introAudioUrl);
    audio.loop = false;
    audio.muted = false;
    audio.volume = 1;
    introAudioRef.current = audio;

    audio.onended = () => {
      bgNarration.current.volume = 0.5;
      setAudioEnded(true);
    };

    bgNarration.current.volume = 0.1;

    audio.play().catch(() => {
      const clickHandler = () => {
        audio.play().catch(() => { });
        window.removeEventListener("click", clickHandler);
      };
      window.addEventListener("click", clickHandler);
    });
  };

  const stopIntroAudio = () => {
    if (introAudioRef.current) {
      introAudioRef.current.pause();
      introAudioRef.current.currentTime = 0;
      introAudioRef.current = null;
      bgNarration.current.volume = 0.5;
    }
  };

  const handleVideoEnded = () => {
    setVideoEndedOnce(true);

    // If audio is still running → loop video
    if (!audioEnded) {
      if (playerRef.current) playerRef.current.seekTo(0);
      return;
    }

    // If both ended (handled via effect), but still here as safety:
    stopIntroAudio();
    navigateToLanding();
  };

  const startBackgroundNarrationAudio = () => {
    if (bgNarration.current) return;
    const bgNarrationAudio = new Audio("https://res.cloudinary.com/giglantis/video/upload/v1777371784/11_Outro_Musiconly_Master_m8fxt9.mp3");
    window.__BG_AUDIO__ = bgNarrationAudio;
    bgNarrationAudio.loop = true; // keep it soft
    bgNarrationAudio.volume = 0.5; // keep it soft
    bgNarration.current = bgNarrationAudio;
    bgNarrationAudio.play().catch(() => { });
  };

  const stopAllAudio = () => {
    stopIntroAudio();

    if (bgNarration.current) {
      bgNarration.current.pause();
      bgNarration.current.currentTime = 0;
      bgNarration.current = null;
    }
  };

  return (
    <div className="video-page-container mob-center-align">
      {!playVideo && <div className="loading-black-bg"></div>}

      <div className="video-bg">
        <ReactPlayer
          ref={playerRef}
          url={videoUrl}
          className="video-container-video"
          loop={false}
          playing={playVideo}
          muted={true}
          controls={false}
          playsinline={true}
          onReady={videoReady}
          onEnded={handleVideoEnded}
        />

        <div
          id="startTourBtnContainer"
          className="start-btn-container hide-start-btn-container"
        >
          <Button
            className="start-tour-btn"
            variant="contained"
            onClick={() => {
              stopIntroAudio();
              navigateToLanding();
            }}
          >
            START TOUR
          </Button>
        </div>
      </div>
    </div>
  );
};

export default Video;
